import { useState } from "react";
import { showToast } from "@/app/global-components/toaster";
import { useTranslate } from "@/locales";
import { useLocale } from "@/middleware";
import { getData } from "@/lib/utils";
import api from "@/store/api";

interface DirectOrderData {
  itemId: string;
  quantity: number;
  price: number;
  addressId: string;
  notes?: string;
  paymentInvoice?: File;
}

interface DirectOrderResponse {
  isSuccess: boolean;
  data?: {
    id: string;
    status: string;
  };
  message?: string;
}

const useDirectOrder = () => {
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const { locale } = useLocale();
  const { t } = useTranslate(locale);

  const createDirectOrder = async (
    orderData: DirectOrderData
  ): Promise<DirectOrderResponse> => {
    try {
      setIsLoading(true);
      setError(null);
      const Token = getData("tokens")?.accessToken;
      if (!Token) {
        throw new Error("Authentication token is missing");
      }

      const formData = new FormData();

      formData.append("itemId", orderData.itemId);
      formData.append("quantity", orderData.quantity.toString());
      formData.append("price", orderData.price.toString());
      formData.append("addressId", orderData.addressId);

      if (orderData.notes) {
        formData.append("notes", orderData.notes);
      }

      if (orderData.paymentInvoice) {
        formData.append("paymentInvoice", orderData.paymentInvoice);
      }

      const response = await api.post("direct-orders", formData, {
        headers: {
          Authorization: `Bearer ${Token}`,
          "Content-Type": "multipart/form-data",
        },
      });

      if (response.data.isSuccess) {
        return {
          isSuccess: true,
          data: response.data.data,
        };
      } else {
        throw new Error(
          response.data.message || "Failed to create direct order"
        );
      }
    } catch (err: any) {
      console.error(err);

      setError(
        err.response?.data?.message ||
          "Failed to create direct order. Please try again."
      );
      showToast(
        err.response?.data?.message || t("Alert.OrderTryAgain"),
        "error"
      );
      return {
        isSuccess: false,
        message: err.response?.data?.message || t("Alert.OrderTryAgain"),
      };
    } finally {
      setIsLoading(false);
    }
  };

  const updateStatus = async (id: string, values: { status: string }) => {
    try {
      setIsLoading(true);
      setError(null);
      const Token = getData("tokens")?.accessToken;
      if (!Token) {
        throw new Error("Authentication token is missing");
      }

      const response = await api.patch(`direct-orders/${id}/status`, values, {
        headers: {
          Authorization: `Bearer ${Token}`,
        },
      });
      if (response.data.isSuccess) {
        return {
          isSuccess: true,
          data: response.data.data,
        };
      } else {
        throw new Error(response.data.message || "Failed to create order");
      }
    } catch (err: any) {
      console.error(err);

      setError(
        err.response?.data?.message ||
          "Failed to update order. Please try again."
      );
      showToast(
        err.response?.data?.message || t("Alert.OrderTryAgain"),
        "error"
      );
      return {
        isSuccess: false,
        message: err.response?.data?.message || t("Alert.OrderTryAgain"),
      };
    } finally {
      setIsLoading(false);
    }
  };

  const fetchDirectOrder = async (id: string) => {
    try {
      setIsLoading(true);
      setError(null);
      const Token = getData("tokens")?.accessToken;
      if (!Token) {
        throw new Error("Authentication token is missing");
      }

      const response = await api.get(`direct-orders/${id}`, {
        headers: {
          Authorization: `Bearer ${Token}`,
        },
      });

      return response.data;
    } catch (err: any) {
      console.error(err);

      setError("Failed to fetch order. Please try again.");
      return null;
    } finally {
      setIsLoading(false);
    }
  };

  const getMyOrders = async (pageSize: number = 10, pageNumber: number = 1) => {
    try {
      setIsLoading(true);
      setError(null);
      const Token = getData("tokens")?.accessToken;
      if (!Token) {
        throw new Error("Authentication token is missing");
      }

      const response = await api.get(
        `/direct-orders/my-orders?pageSize=${pageSize}&pageNumber=${pageNumber}`,
        {
          headers: {
            Authorization: `Bearer ${Token}`,
          },
        }
      );

      return response.data;
    } catch (err: any) {
      console.error(err);

      setError("Failed to fetch orders. Please try again.");
      return null;
    } finally {
      setIsLoading(false);
    }
  };

  return {
    createDirectOrder,
    updateStatus,
    fetchDirectOrder,
    getMyOrders,
    isLoading,
    error,
  };
};

export default useDirectOrder;
